import  { useState, useEffect, useMemo, useRef } from 'react';
import { Wifi, Users, Gauge, Search, ChevronLeft, ChevronRight, Filter } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useApp } from "@/contexts/AppContext";
import { HOTSPOTS_HOTSPOTS, HOTSPOTS_GRAPH, 
  HOTSPOTS_LOCATION,
  HOTSPOTS_SEARCH,  HOTSPOT_STATICTICS } from '../../api/services/adminservice';
import { useTranslation } from 'react-i18next';

const getWifiMarkerIcon = (theme: string) => {
  const isDark = theme === 'dark';
  const bgColor = isDark ? '#3b82f6' : '#2563eb';
  const html = `
    <div style="background-color: ${bgColor}; color: white; border-radius: 50%; width: 32px; height: 32px; display: flex; align-items: center; justify-content: center; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06); border: 2px solid ${isDark ? '#1f2937' : 'white'};">
      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M5 12.55a11 11 0 0 1 14.08 0"/>
        <path d="M1.42 9a16 16 0 0 1 21.16 0"/>
        <path d="M8.53 16.11a6 6 0 0 1 6.95 0"/>
        <line x1="12" x2="12.01" y1="20" y2="20"/>
      </svg>
    </div>
  `;
  return L.divIcon({
    html,
    className: 'custom-wifi-marker',
    iconSize: [32, 32],
    iconAnchor: [16, 16],
    popupAnchor: [0, -16]
  });
};

const isValidLatLng = (lat: number, lng: number) =>
  Number.isFinite(lat) &&
  Number.isFinite(lng) &&
  Math.abs(lat) <= 90 &&
  Math.abs(lng) <= 180;

const escapeText = (value: string) =>
  value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");


const AdminHotspot = () => {
    const { theme } = useApp();
  const { t } = useTranslation();
  
  function getPagination(currentPage, totalPages) {
    const MAX_VISIBLE = 9;
  
    const range = (start, end) =>
      Array.from({ length: end - start + 1 }, (_, i) => start + i);
  
    if (totalPages <= MAX_VISIBLE) {
      return range(1, totalPages);
    }
  
    const pages = [];
  
    pages.push(...range(1, 4));
  
    if (currentPage > 5) {
      pages.push("...");
    }
  
    const middleStart = Math.max(5, currentPage - 1);
    const middleEnd = Math.min(totalPages - 4, currentPage + 1);
  
    pages.push(...range(middleStart, middleEnd));
  
    if (currentPage < totalPages - 4) {
      pages.push("...");
    }
  
    pages.push(...range(totalPages - 3, totalPages));
  
     return [...new Set(pages)];
  }

  const [usageData, setusageData] = useState([
   
  ]);
  const [searchhotpot, setsearchhotpot] = useState("");
  const [hotspotloading, sethotspotloading] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isStatusOpen, setIsStatusOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState("All");
  const [statsLoading, setStatsLoading] = useState(true);
  const [hotspotStats, sethotspotStats] = useState({
    averageSpeed: 0,
    connectedUsers: 0,
    totalHotspots: 0
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
    hasNext: false,
    hasPrevious: false
  });
  const [hotspots, setHotspots] = useState(null);
  const [hotspotLocations, setHotspotLocations] = useState([]);
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markersLayerRef = useRef<L.LayerGroup | null>(null);
  const [isMapDropdownOpen, setIsMapDropdownOpen] = useState(false);

  const isDark = theme === 'dark';
  const todayDate = new Date().toISOString().split('T')[0];

  async function fetchHotspots(page = 1, filteredTimeData?: {
    filters: { status: string; startDate: string; endDate: string };
    format: string;
  }) {
    try {
      const response = await HOTSPOTS_HOTSPOTS({page: page, limit: 10}, filteredTimeData);
      const resData = (response as unknown as { data?: { hotspots?: Record<string, unknown>[], pagination?: { currentPage: number; totalPages: number; totalItems: number; itemsPerPage: number; hasNext: boolean; hasPrevious: boolean; } } }).data || {};
      setHotspots((resData.hotspots as unknown[]) || []);
      setPagination(resData.pagination || {
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 10,
        hasNext: false,
        hasPrevious: false
      });
      console.log('Hotspots data:', response);

    } catch (error: unknown) {
      console.error('Error fetching hotspots:', error);
    }
  }
  async function fetchHotspotGraph() {
    try {
      const data = await HOTSPOTS_GRAPH({period: 'week'});
      setusageData(((data as unknown as Record<string, unknown>).chartData as unknown as Record<string, unknown>[]) || []);
      console.log('Hotspot graph data:', data);

    } catch (error: unknown) {
      console.error('Error fetching hotspots:', error);
    }
  }

  async function fetchStatistics() {
    try {
      setStatsLoading(true);
      const data = await HOTSPOT_STATICTICS();
      console.log('Hotspots data:', data);
      const statsObj = (data as unknown as { data?: { averageSpeed?: number; connectedUsers?: number; totalHotspots?: number; } }).data;
      sethotspotStats({
        averageSpeed: statsObj?.averageSpeed || 0,
        connectedUsers: statsObj?.connectedUsers || 0,
        totalHotspots: statsObj?.totalHotspots || 0
      });
    } catch (error: unknown) {
      console.error('Error fetching hotspots:', error);
    } finally {
      setStatsLoading(false);
    }
  }

  async function fetchHotspotLocation() {
    try {
      const data = await HOTSPOTS_LOCATION();
      setHotspotLocations(((data as unknown as Record<string, unknown>)?.locations as unknown as Record<string, unknown>[]) || []);
      console.log('Hotspots location data:', data);
    } catch (error: unknown) {
      console.error('Error fetching hotspots location:', error);
    }
  }

  async function fetchHotspotSearch() {
    try {
      sethotspotloading(true);
      const response = await HOTSPOTS_SEARCH({search: searchhotpot, page: 1, limit: 10});
      const resData = (response as unknown as { data?: { hotspots?: Record<string, unknown>[] } }).data || {};
      setHotspots((resData.hotspots as unknown[]) || []);

      console.log('Hotspots data:', response);
    } catch (error: unknown) {
      console.error('Error fetching hotspots:', error);
    } finally {
      sethotspotloading(false);
    }
  }
  
  useEffect(() => {
    async function fetchData() {
    await fetchHotspots();
    await fetchStatistics();
    await fetchHotspotGraph();
    await fetchHotspotLocation();
    }
    fetchData();
  }, []);

  const validLocations = useMemo(
    () =>
      (hotspotLocations || []).filter((location) => {
        const lat = Number(location?.latitude);
        const lng = Number(location?.longitude);
        if (!isValidLatLng(lat, lng)) return false;
        if (lat === 0 && lng === 0) return false;
        return true;
      }),
    [hotspotLocations]
  );

  const mapBounds = useMemo(() => {
    if (validLocations.length === 0) return null;
    const points = validLocations.map((loc) => [
      Number(loc.latitude),
      Number(loc.longitude)
    ]) as [number, number][];
    return L.latLngBounds(points);
  }, [validLocations]);

  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;

    const mapInstance = L.map(mapContainerRef.current, {
      center: [0, 0],
      zoom: 2,
      zoomControl: true
    });

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
    }).addTo(mapInstance);

    const markersLayer = L.layerGroup().addTo(mapInstance);
    mapRef.current = mapInstance;
    markersLayerRef.current = markersLayer;

    return () => {
      mapInstance.remove();
      mapRef.current = null;
      markersLayerRef.current = null;
    };
  }, []);

  useEffect(() => {
    const mapInstance = mapRef.current;
    const markersLayer = markersLayerRef.current;

    if (!mapInstance || !markersLayer) return;

    markersLayer.clearLayers();

    if (validLocations.length === 0) {
      mapInstance.setView([0, 0], 2);
      return;
    }

    validLocations.forEach((location) => {
      const lat = Number(location.latitude);
      const lng = Number(location.longitude);
      const marker = L.marker([lat, lng], { icon: getWifiMarkerIcon(theme) });

      const name = escapeText(location.name || "Unnamed Hotspot");
      const address = escapeText(location.address || "No address");
      const city = escapeText(location.city || "No city");
      const status = escapeText(location.status || "unknown");
      const statusLabel = t('admin.hotspots.map.status', { defaultValue: 'Status' });

      marker.bindPopup(
        `<div style="font-size:12px;line-height:1.4;">
          <div style="font-weight:600;">${name}</div>
          <div>${address}, ${city}</div>
          <div>${statusLabel}: ${status}</div>
        </div>`
      );
      marker.on('mouseover', function() {
        marker.openPopup();
      });
      marker.on('mouseout', function() {
        marker.closePopup();
      });
      marker.addTo(markersLayer);
    });

    if (mapBounds) {
      mapInstance.fitBounds(mapBounds, { padding: [20, 20], maxZoom: 13 });
    }
  }, [validLocations, mapBounds, theme]);

  
    useEffect(() => {
      if (searchhotpot.length === 0 || searchhotpot.length > 2) {
        if(searchhotpot === ""){
          fetchHotspots(1);
        } else  {
          fetchHotspotSearch();
        }
      }
    }, [searchhotpot]);

    return (
    <div className={`min-h-screen ${isDark ? 'bg-black' : 'bg-gray-50'}`}>
     

     

      <div className="p-4 sm:p-6 lg:p-8">
        {/* Metrics Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8">
          {/* Total Active Hotspots */}
          <div className={`${isDark ? 'bg-[#333436]' : 'bg-white border border-gray-200'} rounded-lg p-4 sm:p-6 shadow-sm`}>
            <div className="flex items-start justify-between">
              <div>
                <p className={`text-xs sm:text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-1`}>{t('admin.hotspots.metrics.totalActiveHotspots', { defaultValue: 'Total Active Hotspots' })}</p>
                <div className="flex items-baseline gap-2 mt-1">
                  {statsLoading ? (
                    <div className={`h-8 w-16 rounded animate-pulse ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`}></div>
                  ) : (
                    <span className={`text-2xl sm:text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{hotspotStats?.totalHotspots || 0}</span>
                  )}
                </div>
              </div>
              <div className={`${isDark ? 'bg-gray-700' : 'bg-gray-100'} p-2 rounded-lg`}>
                <Wifi className={`w-4 h-4 sm:w-5 sm:h-5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`} />
              </div>
            </div>
          </div>

          {/* Connected Users */}
          <div className={`${isDark ? 'bg-[#333436]' : 'bg-white border border-gray-200'} rounded-lg p-4 sm:p-6 shadow-sm`}>
            <div className="flex items-start justify-between">
              <div>
                <p className={`text-xs sm:text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-1`}>{t('admin.hotspots.metrics.connectedUsers', { defaultValue: 'Connected Users' })}</p>
                <div className="flex items-baseline gap-2 mt-1">
                  {statsLoading ? (
                    <div className={`h-8 w-16 rounded animate-pulse ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`}></div>
                  ) : (
                    <span className={`text-2xl sm:text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{hotspotStats?.connectedUsers || 0}</span>
                  )}
                </div>
              </div>
              <div className={`${isDark ? 'bg-green-900' : 'bg-green-100'} p-2 rounded-lg`}>
                <Users className={`w-4 h-4 sm:w-5 sm:h-5 ${isDark ? 'text-green-300' : 'text-green-600'}`} />
              </div>
            </div>
          </div>

          {/* Average Speed */}
          <div className={`${isDark ? 'bg-[#333436]' : 'bg-white border border-gray-200'} rounded-lg p-4 sm:p-6 shadow-sm sm:col-span-2 lg:col-span-1`}>
            <div className="flex items-start justify-between">
              <div>
                <p className={`text-xs sm:text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-1`}>{t('admin.hotspots.metrics.averageSpeed', { defaultValue: 'Average Speed' })}</p>
                <div className="flex items-baseline gap-2 mt-1">
                  {statsLoading ? (
                    <div className={`h-8 w-24 rounded animate-pulse ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`}></div>
                  ) : (
                    <span className={`text-2xl sm:text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{hotspotStats?.averageSpeed || 0} Mbps</span>
                  )}
                </div>
              </div>
              <div className={`${isDark ? 'bg-purple-900' : 'bg-purple-100'} p-2 rounded-lg`}>
                <Gauge className={`w-4 h-4 sm:w-5 sm:h-5 ${isDark ? 'text-purple-300' : 'text-purple-600'}`} />
              </div>
            </div>
          </div>
        </div>

        {/* Map and Chart Section */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8">
          {/* Map */}
          <div className={`${isDark ? 'bg-[#333436]' : 'bg-white border border-gray-200'} rounded-lg overflow-hidden shadow-sm`}>
            <div className="relative z-0 h-80 lg:h-full min-h-[400px]">
              <div className="absolute top-4 left-12 z-20 pointer-events-auto">
                <button
                  type="button"
                  onClick={() => setIsMapDropdownOpen((prev) => !prev)}
                  className="bg-blue-600 text-white px-3 py-1.5 rounded-md text-sm font-medium flex items-center gap-2"
                >
                  {t('admin.hotspots.map.hotspotsLocations', { defaultValue: 'Hotspots locations' })}
                  <ChevronLeft className={`w-3 h-3 rotate-[-90deg] transition-transform ${isMapDropdownOpen ? 'rotate-[90deg]' : ''}`} />
                </button>
                {isMapDropdownOpen && (
                  <div className={`mt-2 w-64 max-h-72 overflow-auto rounded-lg shadow-lg border ${
                    isDark ? 'bg-[#1a1b1d] border-gray-600 text-gray-200' : 'bg-white border-gray-200 text-gray-700'
                  }`}>
                    {hotspotLocations?.length === 0 ? (
                      <div className="px-3 py-2 text-sm">{t('admin.hotspots.map.noHotspotsAvailable', { defaultValue: 'No hotspots available' })}</div>
                    ) : (
                      hotspotLocations.map((location) => (
                        <div
                          key={location.id}
                          className={`px-3 py-2 text-sm border-b last:border-b-0 ${
                            isDark ? 'border-gray-700' : 'border-gray-200'
                          }`}
                        >
                          <div className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {location.name || t('admin.hotspots.map.unnamedHotspot', { defaultValue: 'Unnamed Hotspot' })}
                          </div>
                          <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                            {location.address || t('admin.hotspots.map.noAddress', { defaultValue: 'No address' })}, {location.city || t('admin.hotspots.map.noCity', { defaultValue: 'No city' })}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
              <div
                ref={mapContainerRef}
                className="w-full h-full"
                style={{ zIndex: 0 }}
              />
              {validLocations.length === 0 && (
                <div className={`absolute inset-0 z-10 flex items-center justify-center ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                  {t('admin.hotspots.map.noValidCoordinates', { defaultValue: 'No valid hotspot coordinates to display' })}
                </div>
              )}
            </div>
          </div>

          {/* Usage Statistics Chart */}
          <div className={`${isDark ? 'bg-[#333436]' : 'bg-white border border-gray-200'} rounded-lg p-4 sm:p-6 shadow-sm lg:col-span-2`}>
            <h2 className={`text-lg sm:text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} mb-4 sm:mb-6`}>{t('admin.hotspots.usageStatistics', { defaultValue: 'Usage Statistics' })}</h2>
            
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={usageData}>
                  <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#3a3b3d' : '#e5e7eb'} />
                  <XAxis 
                    dataKey="day" 
                    tick={{ fill: isDark ? '#6b7280' : '#6b7280', fontSize: 11 }}
                    axisLine={{ stroke: isDark ? '#3a3b3d' : '#e5e7eb' }}
                  />
                  <YAxis 
                    tick={{ fill: isDark ? '#6b7280' : '#6b7280', fontSize: 11 }}
                    axisLine={{ stroke: isDark ? '#3a3b3d' : '#e5e7eb' }}
                    domain={[0, 250]}
                    ticks={[0, 50, 100, 150, 200, 250]}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: isDark ? '#1a1b1d' : 'white', 
                      border: `1px solid ${isDark ? '#3a3b3d' : '#e5e7eb'}`,
                      borderRadius: '8px',
                      fontSize: '12px',
                      color: isDark ? '#ffffff' : '#000000'
                    }}
                  />
                  <Legend 
                    verticalAlign="top" 
                    height={36}
                    iconType="circle"
                    wrapperStyle={{ fontSize: '12px', color: isDark ? '#9ca3af' : '#6b7280' }}
                  />
                  <Line
                    type="monotone"
                    dataKey="user"
                    name={t('admin.hotspots.chart.user', { defaultValue: 'User' })}
                    stroke="#3b82f6"
                    strokeWidth={2}
                    dot={{ fill: '#3b82f6', r: 4 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="speed"
                    name={t('admin.hotspots.chart.speed', { defaultValue: 'Speed' })}
                    stroke="#8b5cf6"
                    strokeWidth={2}
                    dot={{ fill: '#8b5cf6', r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Hotspots List */}
        <div className={`${isDark ? 'bg-[#333436]' : 'bg-white border border-gray-200'} rounded-lg shadow-sm`}>
          <div className={`p-4 sm:p-6 ${isDark ? 'border-gray-700' : 'border-gray-200'} border-b`}>
            <h2 className={`text-lg sm:text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} mb-4`}>{t('admin.hotspots.list.title', { defaultValue: 'Hotspots List' })}</h2>
            
            {/* Search and Filter */}
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1 relative">
                <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                <input
                  type="text"
                  placeholder={t('admin.hotspots.list.searchPlaceholder', { defaultValue: 'Search products...' })}
                  className={`w-full pl-9 sm:pl-10 pr-4 py-2 sm:py-2.5 ${
                    isDark 
                      ? 'bg-[#1a1b1d] border-gray-600 text-white placeholder-gray-500' 
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                  } border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm`}
                  onChange={(e) => {
                    // Handle search input change
                    setsearchhotpot(e.target.value);
                  }}
                />
              </div>
              <div className="relative">
                <button
                  className={`px-4 py-2 sm:py-2.5 ${
                    isDark 
                      ? 'border-gray-600 bg-[#1a1b1d] hover:bg-gray-700 text-white' 
                      : 'border-gray-300 bg-white hover:bg-gray-50 text-gray-700'
                  } border rounded-lg flex items-center justify-center gap-2`}
                  onClick={() => setIsStatusOpen((prev) => !prev)}
                >
                  <span className="text-sm font-medium">
                    {statusFilter === 'All'
                      ? t('admin.common.all', { defaultValue: 'All' })
                      : statusFilter === 'Active'
                        ? t('admin.common.active', { defaultValue: 'Active' })
                        : t('admin.common.inactive', { defaultValue: 'Inactive' })}
                  </span>
                  <ChevronLeft className="w-4 h-4 rotate-[-90deg]" />
                </button>
                {isStatusOpen && (
                  <div className={`absolute mt-2 w-full min-w-[140px] rounded-lg shadow-lg z-20 ${
                    isDark ? 'bg-[#1a1b1d] border border-gray-600' : 'bg-white border border-gray-200'
                  }`}>
                    {["All", "Active", "Inactive"].map((status) => (
                      <button
                        key={status}
                        className={`w-full text-left px-3 py-2 text-sm ${
                          isDark 
                            ? 'text-gray-200 hover:bg-gray-700' 
                            : 'text-gray-700 hover:bg-gray-50'
                        }`}
                        onClick={() => {
                          setStatusFilter(status);
                          setIsStatusOpen(false);
                          const filteredTimeData = {
                            filters: {
                              status: status,
                              startDate: startDate,
                              endDate: endDate
                            },
                            format: "csv"
                          };
                          fetchHotspots(1, filteredTimeData);
                        }}
                      >
                        {status === 'All'
                          ? t('admin.common.all', { defaultValue: 'All' })
                          : status === 'Active'
                            ? t('admin.common.active', { defaultValue: 'Active' })
                            : t('admin.common.inactive', { defaultValue: 'Inactive' })}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <button className={`px-4 py-2 sm:py-2.5 ${
                isDark 
                  ? 'bg-white text-black hover:bg-gray-200' 
                  : 'bg-black text-white hover:bg-gray-800'
              } rounded-lg flex items-center justify-center gap-2`}
              onClick={() => setIsFilterOpen(true)}
              >
                <Filter className="w-4 h-4" />
                <span className="text-sm font-medium">{t('admin.hotspots.list.filter', { defaultValue: 'Filter' })}</span>
              </button>
            </div>
          </div>
          
          {/* Filter Popup */}
          {isFilterOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center">
              <div 
                className="absolute inset-0 bg-black/60"
                onClick={() => setIsFilterOpen(false)}
              />
              <div className={`relative w-[92%] max-w-md rounded-lg p-4 sm:p-6 shadow-xl ${
                isDark ? 'bg-[#1a1b1d] border border-gray-700' : 'bg-white border border-gray-200'
              }`}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className={`text-base sm:text-lg font-semibold ${
                    isDark ? 'text-white' : 'text-gray-900'
                  }`}>{t('admin.hotspots.filter.filterByTime', { defaultValue: 'Filter by Time' })}</h3>
                  <button
                    className={`text-xs sm:text-sm px-2 py-1 rounded ${
                      isDark ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-100'
                    }`}
                    onClick={() => setIsFilterOpen(false)}
                  >
                    {t('admin.hotspots.filter.close', { defaultValue: 'Close' })}
                  </button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className={`block text-xs sm:text-sm mb-1 ${
                      isDark ? 'text-gray-300' : 'text-gray-600'
                    }`}>
                      {t('admin.hotspots.filter.startDate', { defaultValue: 'Start Date' })}
                    </label>
                    <input
                      type="date"
                      max={todayDate}
                      placeholder="Start date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      onFocus={(e) => {
                        e.currentTarget.showPicker?.();
                      }}
                      onKeyDown={(e) => {
                        e.preventDefault();
                      }}
                      className={`w-full px-3 py-2 rounded-lg border text-sm ${
                        isDark 
                          ? 'bg-[#0f1011] border-gray-600 text-white' 
                          : 'bg-white border-gray-300 text-gray-900'
                      }`}
                    />
                  </div>
                  <div>
                    <label className={`block text-xs sm:text-sm mb-1 ${
                      isDark ? 'text-gray-300' : 'text-gray-600'
                    }`}>
                      {t('admin.hotspots.filter.endDate', { defaultValue: 'End Date' })}
                    </label>
                    <input
                      type="date"
                      max={todayDate}
                      placeholder="End date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      onFocus={(e) => {
                        e.currentTarget.showPicker?.();
                      }}
                      onKeyDown={(e) => {
                        e.preventDefault();
                      }}
                      className={`w-full px-3 py-2 rounded-lg border text-sm ${
                        isDark 
                          ? 'bg-[#0f1011] border-gray-600 text-white' 
                          : 'bg-white border-gray-300 text-gray-900'
                      }`}
                    />
                  </div>
                </div>
                <div className="mt-4 flex items-center justify-end gap-2">
                  <button
                    className={`px-3 py-2 rounded-lg text-sm ${
                      isDark 
                        ? 'bg-gray-700 text-white hover:bg-gray-600' 
                        : 'bg-gray-200 text-gray-900 hover:bg-gray-300'
                    }`}
                    onClick={() => {
                      setStartDate("");
                      setEndDate("");
                    }}
                  >
                    {t('admin.hotspots.filter.clear', { defaultValue: 'Clear' })}
                  </button>
                  <button
                    className={`px-3 py-2 rounded-lg text-sm ${
                      isDark 
                        ? 'bg-white text-black hover:bg-gray-200' 
                        : 'bg-black text-white hover:bg-gray-800'
                    }`}
                    onClick={() => {
                      const filteredTimeData = {
                        filters: {
                          status: statusFilter,
                          startDate: startDate,
                          endDate: endDate
                        },
                        format: "csv"
                      };
                      fetchHotspots(1, filteredTimeData);
                      setIsFilterOpen(false);
                    }}
                  >
                    {t('admin.hotspots.filter.apply', { defaultValue: 'Apply' })}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px]">
              <thead className={`${isDark ? 'bg-[#2a2b2d] border-gray-700' : 'bg-gray-50 border-gray-200'} border-b`}>
                <tr>
                  <th className={`px-3 sm:px-6 py-3 text-left text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'} uppercase tracking-wider`}>
                    {t('admin.hotspots.table.hotspotId', { defaultValue: 'Hotspot ID' })}
                  </th>
                  <th className={`px-3 sm:px-6 py-3 text-left text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'} uppercase tracking-wider`}>
                    {t('admin.hotspots.table.owner', { defaultValue: 'Owner' })}
                  </th>
                  <th className={`px-3 sm:px-6 py-3 text-left text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'} uppercase tracking-wider`}>
                    {t('admin.hotspots.table.location', { defaultValue: 'Location' })}
                  </th>
                  <th className={`px-3 sm:px-6 py-3 text-left text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'} uppercase tracking-wider`}>
                    {t('admin.hotspots.table.status', { defaultValue: 'Status' })}
                  </th>
                  <th className={`px-3 sm:px-6 py-3 text-left text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'} uppercase tracking-wider`}>
                    {t('admin.hotspots.table.users', { defaultValue: 'Users' })}
                  </th>
                  <th className={`px-3 sm:px-6 py-3 text-left text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'} uppercase tracking-wider`}>
                    {t('admin.hotspots.table.speed', { defaultValue: 'Speed' })}
                  </th>
                </tr>
              </thead>
              <tbody className={`${isDark ? 'bg-[#333436] divide-gray-700' : 'bg-white divide-gray-200'} divide-y`}>

                {hotspots?.length === 0 && hotspotloading === false && (
                  <tr>
                    <td colSpan={6} className={`px-3 sm:px-6 py-4 text-center ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      {t('admin.hotspots.table.noHotspotsFound', { defaultValue: 'No hotspots found.' })}
                    </td>
                  </tr>
                )}
                {hotspots && hotspots.length > 0 && hotspotloading === false && hotspots.map((hotspot, idx) => (
                  <tr key={idx} className={isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}>
                    <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                      <div className={`text-xs sm:text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {hotspot.hotspotId}
                      </div>
                    </td>
                    <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {hotspot?.owner ? <div className={`w-8 h-8 ${isDark ? 'bg-gray-600' : 'bg-gray-200'} rounded-full flex items-center justify-center mr-2 sm:mr-3 flex-shrink-0`}>
                          <span className={`text-xs sm:text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-600'}`}>{hotspot?.owner?.name.split(' ')[0][0] + "" +hotspot?.owner?.name.split(' ')[1][0]}</span>
                        </div> : <br/>}
                        <div className="min-w-0">
                          <div className={`text-xs sm:text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'} truncate`}>{hotspot?.owner?.name}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                      <div className={`text-xs sm:text-sm ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>{hotspot.averageSpeed}</div>
                    </td>
                    <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 sm:px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        ['active', 'online'].includes((hotspot.status as string || '').toLowerCase())
                          ? isDark ? 'bg-green-900 text-green-300' : 'bg-green-100 text-green-800'
                          : isDark ? 'bg-red-900 text-red-300' : 'bg-red-100 text-red-800'
                      }`}>
                        {hotspot.status ? (hotspot.status as string).charAt(0).toUpperCase() + (hotspot.status as string).slice(1) : ''}
                      </span>
                    </td>
                    <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                      <div className={`text-xs sm:text-sm ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>{hotspot.location.address}</div>
                    </td>
                    <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                      <div className={`text-xs sm:text-sm ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>{hotspot.speed}</div>
                    </td>
                  </tr>
                ))}

                {hotspotloading && (
                  <tr>
                    <td colSpan={6} className={`px-3 sm:px-6 py-4 text-center ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      {t('admin.common.loading', { defaultValue: 'Loading...' })}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className={`px-3 sm:px-6 py-4 ${isDark ? 'border-gray-700' : 'border-gray-200'} border-t flex flex-col sm:flex-row items-center justify-between gap-3`}>
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  if (pagination.currentPage > 1) {
                    fetchHotspots(pagination.currentPage - 1);
                  }
                }}
                disabled={!pagination.hasPrevious}
                className={`px-3 py-1 ${
                isDark 
                  ? 'bg-transparent text-gray-300 border border-gray-600' 
                  : 'bg-transparent text-gray-700 border border-gray-300'
              } text-xs sm:text-sm rounded ${pagination.hasPrevious ? '' : 'opacity-50 cursor-not-allowed'}`}>
                {t('admin.common.previous', { defaultValue: 'Previous' })}
              </button>
              <span className={`text-xs sm:text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                {pagination.totalItems === 0
                  ? "0 of 0"
                  : `${(pagination.currentPage - 1) * pagination.itemsPerPage + 1} to ${Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems)} of ${pagination.totalItems}`}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button className={`p-2 ${
                isDark 
                  ? 'bg-[#2a2b2d] hover:bg-gray-700' 
                  : 'bg-gray-100 hover:bg-gray-200'
              } rounded-lg`}
              onClick={() => {
                if (pagination.currentPage > 1) {
                  fetchHotspots(pagination.currentPage - 1);
                }
              }}
              disabled={!pagination.hasPrevious}
              >
                <ChevronLeft className={`w-3 h-3 sm:w-4 sm:h-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`} />
              </button>
              {getPagination(pagination.currentPage, pagination.totalPages).map((page, idx) => (
                <button
                  key={`${page}-${idx}`}
                  disabled={page === "..."}
                  onClick={() => {
                    if (page !== "...") {
                      fetchHotspots(page);
                    }
                  }}
                  className={`px-3 py-1 ${
                    page === pagination.currentPage
                      ? isDark 
                        ? 'bg-gray-600 text-white' 
                        : 'bg-gray-200 text-gray-900'
                      : isDark 
                        ? 'bg-[#2a2b2d] hover:bg-gray-700 text-gray-300' 
                        : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                  } rounded-lg text-xs sm:text-sm font-medium ${page === "..." ? 'cursor-default' : ''}`}
                >
                  {page}
                </button>
              ))}
              <button className={`p-2 ${
                isDark 
                  ? 'bg-[#2a2b2d] hover:bg-gray-700' 
                  : 'bg-gray-100 hover:bg-gray-200'
              } rounded-lg`}
              onClick={() => {
                if (pagination.currentPage < pagination.totalPages) {
                  fetchHotspots(pagination.currentPage + 1);
                }
              }}
              disabled={!pagination.hasNext}
              >
                <ChevronRight className={`w-3 h-3 sm:w-4 sm:h-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminHotspot;
